import {action, computed, runInAction} from 'mobx';
import {TechseeBaseController} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-controller';
import {InviteMobileState} from './_contracts/invite-mobile.state';
import get from 'lodash/get';
import * as patterns from '@techsee/techsee-common/lib/constants/utils.patterns.js';
import {FlowType, MeetingMode} from '@techsee/techsee-common/lib/constants/room.constants';
import {RolesConstant} from '@techsee/techsee-common/lib/constants/account.constants';
import TsUtils from '@techsee/techsee-common/lib/utils';
import {SESSION_TYPES} from '../../constants/session.constants';
import {SentByServerEvents} from '../../services/Session/SessionSocketEvents';
import {IAuthService} from '../../services/auth/auth.service';
import {IRoomService} from '@techsee/techsee-client-infra/lib/services/RoomService';
import {IShortUrlService} from '@techsee/techsee-client-infra/lib/services/ShortUrlService';
import {IEventLogService} from '../../services/event-log/event-log.service';
import {IRedirectionService} from '../../services/RedirectionService';
import {ITsAppUserService} from '../../services/TsAppUserService';
import {ISmsService} from '../../services/SmsService';
import {IBrowserUtilsService} from '@techsee/techsee-client-infra/lib/services/BrowserUtilsService';
import {AppState} from '../../app.state';
import {CountrySelectController} from '../../components/ts-country-select/ts-country-select.controller';
import {TsMultiSelectController} from '../../components/ts-multi-select/ts-multi-select.controller';
import {ChangeSettingsModalController} from '../../modals/change-settings-modal/ChangeSettingsModal.controller';
import {ChangeAccountModalController} from '../../modals/change-account-modal/ChangeAccountModal.controller';
import {SimpleModalController} from '../../components/simple-modal/controller';
import {AcceptTermsModalController} from '../../modals/accept-terms-modal/AcceptTermsModal.controller';
import {SmsStatusModalController} from '../../modals/sms-status-modal/SmsStatusModal.controller';
import {LookupNumberController} from '../../states/invite-new/invite-wizard/components/lookup-number/controller';
import {ChangeSettingsModalState} from '../../modals/change-settings-modal/ChangeSettingsModal.state';
import {ChangeAccountModalState} from '../../modals/change-account-modal/ChangeAccountModal.state';
import {AcceptTermsModalState} from '../../modals/accept-terms-modal/AcceptTermsModal.state';
import {SmsStatusModalState} from '../../modals/sms-status-modal/SmsStatusModal.state';

// @ts-ignore - DASHBOARD_URL, CLIENT_START_URL, and BASE_PATH are webpack global constants
declare const DASHBOARD_URL: string;
declare const CLIENT_START_URL: string;
declare const BASE_PATH: string;

import {IObservationsService} from '@techsee/techsee-client-infra/lib/services/ObservationsService';
import {IObservableSession} from '@techsee/techsee-client-infra/lib/services/UserService';

interface ObservableUserOption {
    value: string;
    name: string;
    roomId: string;
    sessionId: string;
}

export interface IInviteMobileControllerDependencies {
    roomService: IRoomService;
    shortUrlService: IShortUrlService;
    eventLogService: IEventLogService;
    redirectionService: IRedirectionService;
    browserUtilsService: IBrowserUtilsService;
    authService: IAuthService;
    tsAppUserService: ITsAppUserService;
    appState: AppState;
    smsService: ISmsService;
    observationsService: IObservationsService;
    tsInterfaceHelperService: any;
    tsLocaleHelper: any;
    environmentService: any;
    chatApi: any;
    eventsLogService: any;
    userService: any;
    window: Window;
    socketManager: any;
    displayTabletAsDesktop: boolean;
}

export class InviteMobileController extends TechseeBaseController {
    readonly state: InviteMobileState;

    private readonly roomService: IRoomService;
    private readonly shortUrlService: IShortUrlService;
    private readonly eventLogService: IEventLogService;
    private readonly redirectionService: IRedirectionService;
    private readonly browserUtilsService: IBrowserUtilsService;
    private readonly authService: IAuthService;
    private readonly tsAppUserService: ITsAppUserService;
    private readonly appState: AppState;
    private readonly smsService: ISmsService;
    private readonly observationsService: IObservationsService;
    private readonly window: Window;
    private readonly tsInterfaceHelperService: any;
    private readonly tsLocaleHelper: any;
    private readonly environmentService: any;
    private readonly chatApi: any;
    private readonly eventsLogService: any;
    private readonly userService: any;
    private readonly socketManager: any;
    private readonly displayTabletAsDesktop: boolean;

    countrySelectController?: CountrySelectController;
    inviteViaController?: TsMultiSelectController;
    sessionTypeController?: TsMultiSelectController;
    languageController?: TsMultiSelectController;
    observableUsersController?: TsMultiSelectController;
    changeSettingsModalController?: ChangeSettingsModalController;
    changeAccountModalController?: ChangeAccountModalController;
    simpleModalController?: SimpleModalController;
    acceptTermsModalController?: AcceptTermsModalController;
    smsStatusModalController?: SmsStatusModalController;
    lookupNumberController?: LookupNumberController;

    constructor(state: InviteMobileState, dependencies: IInviteMobileControllerDependencies) {
        super();
        this.state = state;
        this.roomService = dependencies.roomService;
        this.shortUrlService = dependencies.shortUrlService;
        this.eventLogService = dependencies.eventLogService;
        this.redirectionService = dependencies.redirectionService;
        this.browserUtilsService = dependencies.browserUtilsService;
        this.authService = dependencies.authService;
        this.tsAppUserService = dependencies.tsAppUserService;
        this.appState = dependencies.appState;
        this.smsService = dependencies.smsService;
        this.observationsService = dependencies.observationsService;
        this.tsInterfaceHelperService = dependencies.tsInterfaceHelperService;
        this.tsLocaleHelper = dependencies.tsLocaleHelper;
        this.environmentService = dependencies.environmentService;
        this.chatApi = dependencies.chatApi;
        this.eventsLogService = dependencies.eventsLogService;
        this.userService = dependencies.userService;
        this.window = dependencies.window;
        this.socketManager = dependencies.socketManager;
        this.displayTabletAsDesktop = dependencies.displayTabletAsDesktop;

        // Subscribe to SMS status socket events
        if (this.socketManager?.sessionSocket) {
            this.socketManager.sessionSocket.on(
                SentByServerEvents.SMS_STATUS_NOTIFICATION,
                this.handleSmsStatusNotification
            );
        }
    }

    private handleSmsStatusNotification = (data: any): void => {
        // Create progress callback for SMS status updates
        const progressCb = (message: any) => {
            if (message.data?.customError) {
                this.setSms(false);
                const customError =
                    typeof message.data.customError === 'string'
                        ? {message: message.data.customError, level: 'error'}
                        : message.data.customError;
                this.setCustomError(customError);
            }
            if (message.data?.smsError) {
                this.setSms(false);
                this.setCustomError({
                    message: message.data.smsError.Message,
                    level: message.data.smsError.Level
                });
            }
            if (message.data?.statusMessage) {
                this.setStatusMessage(message.data.statusMessage);
            }
        };
        this.smsService.smsStatusChanged(data, progressCb);
    };

    dispose(): void {
        if (this.socketManager?.sessionSocket) {
            this.socketManager.sessionSocket.off(
                SentByServerEvents.SMS_STATUS_NOTIFICATION,
                this.handleSmsStatusNotification
            );
        }
        super.dispose();
    }

    initializeSubControllers(): void {
        // Create modal states
        const changeSettingsModalState = new ChangeSettingsModalState();
        const changeAccountModalState = new ChangeAccountModalState();
        const acceptTermsModalState = new AcceptTermsModalState();
        const smsStatusModalState = new SmsStatusModalState();

        // Create modal controllers
        this.changeSettingsModalController = new ChangeSettingsModalController(
            changeSettingsModalState,
            this.tsAppUserService
        );

        this.changeAccountModalController = new ChangeAccountModalController(
            changeAccountModalState,
            this.authService,
            this.appState
        );

        this.simpleModalController = new SimpleModalController(
            'unauthorized-modal',
            '',
            this.translate('INVITE.VIEW.UNAUTHORIZED_ERROR_CONTENT'),
            '',
            this.translate('INVITE.VIEW.UNAUTHORIZED_ERROR_BUTTON')
        );

        this.acceptTermsModalController = new AcceptTermsModalController(acceptTermsModalState);

        this.smsStatusModalController = new SmsStatusModalController(
            smsStatusModalState,
            this.chatApi,
            this.environmentService,
            this.displayTabletAsDesktop
        );

        if (get(this.accountData, 'protectedSettings.smsSettings.smsUseNumberLookup')) {
            this.lookupNumberController = new LookupNumberController(
                this.translate,
                this.eventsLogService,
                Boolean(get(this.accountData, 'protectedSettings.keepCustomerMobile'))
            );
        }

        // Create country select controller
        const addCountryCodeToSms = Boolean(get(this.accountData, 'protectedSettings.smsSettings.addCountryCodeToSms'));
        const accountCountryCode = String(get(this.accountData, 'settings.countryCode', ''));
        const selectedCountries = get(this.accountData, 'protectedSettings.selectedCountries', []) as any[];
        const lastUsedTOSCountryCode = this.browserUtilsService.getFromLocalStorage('lastUsedTOSCountryCode');
        this.browserUtilsService.removeFromLocalStorage('lastUsedTOSCountryCode');

        this.countrySelectController = new CountrySelectController(
            this.translate,
            (key: string) => this.browserUtilsService.getFromLocalStorage(key),
            this.useNewInterface,
            addCountryCodeToSms,
            accountCountryCode,
            selectedCountries,
            this.state.sms,
            this.state.countryCodeText,
            (countryCode: string) => this.setCountryCodeText(countryCode)
        );

        this.countrySelectController.initWithData({
            lastUsedTOSCountryCode: lastUsedTOSCountryCode,
            preloadCountryCode: undefined
        });

        // Create invite via controller
        const inviteViaOptions = [];
        if (get(this.accountData, 'settings.allowInviteBySms', true)) {
            inviteViaOptions.push({value: 'SMS', name: this.translate('INVITE.VIEW.SMS')});
        }
        if (get(this.accountData, 'settings.allowSendByEmail', true)) {
            inviteViaOptions.push({value: 'EMAIL', name: this.translate('INVITE.VIEW.EMAIL')});
        }
        if (get(this.accountData, 'settings.allowSessionWithoutSms')) {
            inviteViaOptions.push({value: 'LINK', name: this.translate('INVITE.VIEW.LINK')});
        }

        this.inviteViaController = new TsMultiSelectController(this.useNewInterface, {
            id: 'invite-via',
            inputId: 'invite-via',
            controlName: 'invite-via',
            outputProperties: ['value'],
            selectedProperty: 'selected',
            placeholder: this.translate('INVITE.VIEW.INVITE_VIA'),
            buttonLabel: ['name'],
            itemLabel: ['name'],
            inputModel: inviteViaOptions,
            onChange: (item: any) => this.setInviteMethod(item?.value)
        });

        // Create session type controller
        const cameraModeOnly = Boolean(get(this.accountData, 'settings.cameraModeOnly'));
        const allowOneClickPhotoMode = Boolean(get(this.accountData, 'settings.allowOneClickPhotoMode'));
        const defaultMeetingMode = get(this.accountData, 'settings.defaultMeetingMode');
        const accountOfflineSessions = Boolean(get(this.accountData, 'settings.accountOfflineSessions'));

        let sessionTypeValues = [SESSION_TYPES.LIVE];
        if (!cameraModeOnly && allowOneClickPhotoMode && defaultMeetingMode === MeetingMode.oneClick) {
            sessionTypeValues = [MeetingMode.video, MeetingMode.oneClick];
        }
        if (accountOfflineSessions) {
            sessionTypeValues.push(SESSION_TYPES.OFFLINE);
        }

        const hasLiveMode = sessionTypeValues.indexOf(SESSION_TYPES.LIVE) > -1;
        const sessionTypeOptions = sessionTypeValues.map((sessionType) => ({
            value: sessionType,
            name:
                hasLiveMode || sessionType === SESSION_TYPES.OFFLINE
                    ? this.translate(`INVITE.VIEW.${sessionType}`)
                    : this.translate(`REACT.DASHBOARD.MODE_SWITCH_CONFIRMATION.HEADER.${sessionType}`)
        }));

        this.sessionTypeController = new TsMultiSelectController(this.useNewInterface, {
            id: 'session-type',
            inputId: 'session-type',
            controlName: 'session-type',
            outputProperties: ['value'],
            selectedProperty: 'selected',
            placeholder: '',
            buttonLabel: ['name'],
            itemLabel: ['name'],
            inputModel: sessionTypeOptions,
            outputModel: sessionTypeOptions[0]?.value,
            onChange: (item: any) => this.setSessionType(item?.value)
        });

        // Create language controller
        const clientLanguages = get(this.accountData, 'settings.clientLanguages', []) as Array<{
            name?: string;
            value?: string;
        }>;
        const languageOptions = clientLanguages.map((lang) => ({
            value: String(lang.value ?? lang.name ?? ''),
            name: String(lang.name ?? lang.value ?? '')
        }));

        this.languageController = new TsMultiSelectController(this.useNewInterface, {
            id: 'language-mobile',
            inputId: 'language',
            controlName: 'language-mobile',
            outputProperties: ['value'],
            selectedProperty: 'selected',
            placeholder: this.translate('INVITE.VIEW.LANGUAGE'),
            buttonLabel: ['name'],
            itemLabel: ['name'],
            inputModel: languageOptions,
            onChange: (item: any) => this.setSelectedLanguage(item?.value)
        });

        // Set initial values
        const audioConfig = get(this.accountData, 'protectedSettings.audioStartMode', {}) as Record<string, any>;
        this.setEnableAudio(Boolean(audioConfig?.mobile?.startSessionWithAudio));

        const allowInviteBySms = Boolean(get(this.accountData, 'settings.allowInviteBySms', true));
        const allowSendByEmail = Boolean(get(this.accountData, 'settings.allowSendByEmail', true));
        const allowSessionWithoutSms = Boolean(get(this.accountData, 'settings.allowSessionWithoutSms', true));

        if (allowInviteBySms) {
            this.setInviteMethod('SMS');
            this.setShowMobileTechseeInvite(true);
        } else if (allowSendByEmail) {
            this.setInviteMethod('EMAIL');
            this.setShowMobileTechseeInvite(false);
        } else if (allowSessionWithoutSms) {
            this.setInviteMethod('LINK');
            this.setShowMobileTechseeInvite(false);
        }

        // Load observable users if applicable
        const accountId = this.currentUser?.accountId;
        const userRole = this.currentUser?.role;
        const canObserve = Boolean(this.currentUser?.canObserve);
        const canRetrieveObservableRooms = ![
            RolesConstant.MANAGER,
            RolesConstant.USER,
            RolesConstant.FIELD_SERVICES_ENGINEER
        ].includes(userRole);

        if (accountId && canObserve && canRetrieveObservableRooms) {
            this.userService
                .getObservableByAccount({id: accountId})
                .then((res: IObservableSession[]) => {
                    const data = res;
                    const observableUsers: IObservableSession[] = Array.isArray(data) ? data : [];
                    this.setObservableUsers(observableUsers);
                    this.initializeObservableUsersController(observableUsers);
                })
                .catch(() => {
                    // Silently handle error
                });
        } else {
            // Initialize with empty array
            this.initializeObservableUsersController([]);
        }
    }

    @action
    setInviteMethod(inviteMethod?: string): void {
        this.state.inviteMethod = inviteMethod;
    }

    @action
    setSessionType(sessionType?: string): void {
        this.state.sessionType = sessionType;
    }

    @action
    setSelectedLanguage(selectedLanguage?: string): void {
        this.state.selectedLanguage = selectedLanguage;
    }

    @action
    setObservableUsers(observableUsers: IObservableSession[]): void {
        this.state.observableUsers = observableUsers;
        this.initializeObservableUsersController(observableUsers);
    }

    private initializeObservableUsersController(observableUsers: IObservableSession[]): void {
        const observableUsersOptions: ObservableUserOption[] = observableUsers.map((observer) => ({
            value: String(observer.roomId ?? ''),
            name: String(observer.userName ?? ''),
            roomId: String(observer.roomId ?? ''),
            sessionId: String(observer.sessionId ?? observer.roomId ?? '')
        }));

        if (this.observableUsersController) {
            // Update existing controller's inputModel
            this.observableUsersController.updateConfig({inputModel: observableUsersOptions});
        } else {
            // Create new controller
            this.observableUsersController = new TsMultiSelectController(this.useNewInterface, {
                id: 'observable-users',
                inputId: 'observable-users',
                controlName: 'observable-users',
                outputProperties: ['roomId'],
                selectedProperty: 'selected',
                placeholder: 'Select User...',
                buttonLabel: ['name'],
                itemLabel: ['name'],
                inputModel: observableUsersOptions,
                onChange: (item: ObservableUserOption) => {
                    if (item?.roomId) {
                        this.setObservedRoomId(item.roomId);
                    }
                }
            });
        }

        // Set initial selection if observedRoomId is already set
        if (this.state.observedRoomId && observableUsersOptions.length > 0) {
            const selectedOption = observableUsersOptions.find((option) => option.roomId === this.state.observedRoomId);
            if (selectedOption && this.observableUsersController) {
                this.observableUsersController.selectItem(selectedOption);
            }
        }
    }

    @action
    setCustomerId(customerId: string): void {
        this.state.customerId = customerId;
    }

    @action
    toggleOverlay(): void {
        this.state.isOverlayOpen = !this.state.isOverlayOpen;
    }

    @computed
    get useNewInterface(): boolean {
        return Boolean(this.tsInterfaceHelperService.useNewInterface);
    }

    @computed
    get fullName(): string {
        if (!this.state.currentUser) {
            return '';
        }
        const firstName = get(this.state.currentUser, 'firstName', '');
        const lastName = get(this.state.currentUser, 'lastName', '');
        return `${firstName} ${lastName}`.trim();
    }

    @computed
    get isLive3(): boolean {
        return Boolean(get(this.state.accountData, 'protectedSettings.v3.enabled'));
    }

    @computed
    get isMultiAccountsUser(): boolean {
        return Boolean(get(this.state.currentUser, 'parentSubUserId'));
    }

    @computed
    get isRTL(): boolean {
        return this.tsLocaleHelper.isRTL();
    }

    @computed
    get isIOS(): boolean {
        return Boolean(this.environmentService.isIOS());
    }

    @computed
    private get accountData(): Record<string, any> {
        return this.state.accountData as Record<string, any>;
    }

    @computed
    private get currentUser(): Record<string, any> {
        return this.state.currentUser as Record<string, any>;
    }

    @action
    async changeUserSettings(passwordOnly: boolean = false): Promise<void> {
        if (!this.changeSettingsModalController) {
            return;
        }

        try {
            const updatedUser = await this.changeSettingsModalController.open({
                currentUser: this.state.currentUser,
                accountData: this.state.accountData,
                passwordOnly
            });

            if (updatedUser && this.state.currentUser) {
                runInAction(() => {
                    Object.assign(this.state.currentUser as object, updatedUser);
                });
            }
        } catch {
            // User cancelled or error occurred
        }
    }

    @action
    async switchAccount(): Promise<void> {
        if (!this.changeAccountModalController) {
            return;
        }

        try {
            const result = await this.changeAccountModalController.open({
                currentUser: this.state.currentUser,
                accountData: this.state.accountData
            });

            if (result && result.reload) {
                this.window.location.reload();
            }
        } catch {
            // User cancelled or error occurred
        }
    }

    @action
    async logout(): Promise<void> {
        const accountId = get(this.state.accountData, '_id') as string | undefined;
        const userId = get(this.state.currentUser, '_id') as string | undefined;

        if (!accountId || !userId) {
            this.window.location.href = '/';
            return;
        }

        const windowOrigin = this.window.location.origin;
        const alias = get(this.state.accountData, 'protectedSettings.alias', '') as string;
        const returnURL = `${windowOrigin}${BASE_PATH}${alias}`;

        try {
            const res = await this.authService.logout(accountId, userId, returnURL);
            if (!res?.data) {
                this.window.location.href = '/';
                return;
            }
            this.window.location.href = res.data;
        } catch {
            this.window.location.href = '/';
        }
    }

    @action
    setPhone(phone: string): void {
        this.state.phone = phone;
    }

    @action
    setMeetingCode(meetingCode: string): void {
        this.state.meetingCode = meetingCode;
    }

    @action
    setObservedRoomId(observedRoomId: string): void {
        this.state.observedRoomId = observedRoomId;
    }

    @action
    setCountryCodeText(countryCodeText: string): void {
        this.state.countryCodeText = countryCodeText;
    }

    @action
    setShowMobileTechseeInvite(showMobileTechseeInvite: boolean): void {
        this.state.showMobileTechseeInvite = showMobileTechseeInvite;
    }

    @action
    setEnableAudio(enableAudio: boolean): void {
        this.state.enableAudio = enableAudio;
    }

    @action
    setSms(sms: boolean): void {
        this.state.sms = sms;
    }

    @action
    setStatusMessage(statusMessage?: string): void {
        this.state.statusMessage = statusMessage;
    }

    @action
    setCustomError(customError?: {message: string; level?: string}): void {
        if (customError?.message) {
            this.state.customError = {
                ...customError,
                message: this.translate(customError.message)
            };
        } else {
            this.state.customError = customError;
        }
    }

    private _checkInviteId(): boolean {
        const customerIdSettings = get(this.state.accountData, 'settings.customerId') as
            | {
                  display?: boolean;
                  minLength?: number;
                  maxLength?: number;
              }
            | undefined;
        if (
            customerIdSettings &&
            customerIdSettings !== null &&
            typeof customerIdSettings === 'object' &&
            'display' in customerIdSettings &&
            customerIdSettings.display
        ) {
            const customerId = this.state.customerId || '';
            const {minLength: min = 0, maxLength: max} = customerIdSettings;
            const isInvalid = customerId.length < min || (max !== undefined && customerId.length > max);

            return isInvalid
                ? (this.setCustomError({
                      level: 'error',
                      message:
                          max !== undefined
                              ? this.translate('INVITE.VIEW.ID_LENGTH', {
                                    minLength: String(min),
                                    maxLength: String(max)
                                })
                              : 'INVITE.VIEW.ID_REQUIRED'
                  }),
                  false)
                : true;
        }
        return true;
    }

    private _shouldStartWithAudio(): boolean {
        return this.state.enableAudio;
    }

    private _getSessionStartMode(): MeetingMode | undefined {
        if (
            this.state.sessionType &&
            this.state.sessionType !== SESSION_TYPES.LIVE &&
            this.state.sessionType !== SESSION_TYPES.OFFLINE
        ) {
            // Cast string session types to MeetingMode (e.g., 'SCREEN', 'APPLICATION_SHARING', 'CO_BROWSING')
            return this.state.sessionType as MeetingMode;
        }
        return undefined;
    }

    private _getClientLanguage(): string {
        if (this.state.selectedLanguage) {
            return this.state.selectedLanguage;
        }
        const accountLanguage = get(this.state.accountData, 'settings.language', 'en_US');
        return String(accountLanguage || 'en_US');
    }

    private _validateCountryCodeAndPhone(
        countryCode: string,
        phone: string,
        addCountryCodeToSms: boolean
    ): string | null {
        if (!addCountryCodeToSms) {
            return phone;
        }

        if (!this.countrySelectController?.validateCountryCode(countryCode)) {
            this.setCustomError({message: 'INVITE.CONTROLLER.INVALID_COUNTRY_CODE', level: 'error'});
            return null;
        }

        if (phone.includes('+')) {
            this.setCustomError({message: 'INVITE.CONTROLLER.PLUS_SIGN_NOT_SUPPORTED', level: 'error'});
            return null;
        }

        return countryCode + phone.replace(/^0*/, '');
    }

    private async _sendSMS(phone: string, roomId: string, countryCode: string): Promise<void> {
        // @ts-ignore - CLIENT_START_URL is a webpack global constant
        const clientStartURL = (TsUtils as any).replaceUrlDomain(CLIENT_START_URL, this.window.location.hostname);

        const smsData = {
            phone: phone,
            roomId: roomId,
            link: undefined,
            offline: this.state.sessionType === SESSION_TYPES.OFFLINE,
            chromeDetection: Boolean(get(this.accountData, 'protectedSettings.chromeDetection')),
            cameraModeOnly: Boolean(get(this.accountData, 'settings.cameraModeOnly')),
            showSmsPreview: Boolean(get(this.accountData, 'protectedSettings.showSmsPreview')),
            mobileClientURL: clientStartURL,
            countryCode: countryCode,
            startWithAgentType: this._getSessionStartMode(),
            smsUseNumberLookup: Boolean(get(this.accountData, 'protectedSettings.smsSettings.smsUseNumberLookup')),
            audio: this._shouldStartWithAudio()
        };

        this.browserUtilsService.saveToLocalStorage('phoneNumber', phone);
        this.browserUtilsService.saveToLocalStorage('countryCode', countryCode);
        this.browserUtilsService.saveToLocalStorage('meetingMode', smsData.startWithAgentType || '');

        this.setSms(true);

        const progressCb = (message: any) => {
            if (message.data?.customError) {
                this.setSms(false);
                // Handle both string (translation key) and object formats
                const customError =
                    typeof message.data.customError === 'string'
                        ? {message: message.data.customError, level: 'error'}
                        : message.data.customError;
                this.setCustomError(customError);
            }

            if (message.data?.smsError) {
                this.setSms(false);
                this.setCustomError({
                    message: message.data.smsError.Message,
                    level: message.data.smsError.Level
                });
            }

            if (message.data?.statusMessage) {
                this.setStatusMessage(message.data.statusMessage);
            }
        };

        // Mobile flow: no modal, proceed to dashboard immediately after SMS send
        // Socket events will update when client connects
        const result = await this.smsService.sendSms(smsData, progressCb);

        if (result?.success === false) {
            // Explicit failure - don't proceed to dashboard
            this.setSms(false);
            return;
        }

        // SMS sent successfully - proceed to dashboard (don't wait for delivery confirmation on mobile)
        this.browserUtilsService.saveToLocalStorage('lastUsedTOSCountryCode', countryCode);

        // Log session start event like AngularJS does
        if (this.accountData && this.currentUser) {
            this.eventLogService.oldInviteSessionStarted({
                accountId: this.accountData._id,
                userId: this.currentUser._id,
                userAgent: navigator.userAgent,
                timestamp: new Date().toISOString()
            });
        }

        if (this.state.sessionType === SESSION_TYPES.OFFLINE) {
            this.setStatusMessage('INVITE.CONTROLLER.OFFLINE_SESSION_CREATED');
        }
        this._gotoDashboard(roomId, undefined);
    }

    async launchInviteMobile(type: 'sms' | 'email' | 'whatsapp'): Promise<void> {
        if (!this._checkInviteId()) {
            return;
        }

        this.setCustomError(undefined);

        try {
            const isTechnicianSupervisor = this.currentUser?.role === RolesConstant.TECHNICIAN_SUPERVISOR;

            const shouldStartWithAudio = this._shouldStartWithAudio();

            const countryCode = this.countrySelectController?.getCurrentCountryCode() || '';
            const addCountryCodeToSms = Boolean(
                get(this.accountData, 'protectedSettings.smsSettings.addCountryCodeToSms')
            );

            // For SMS, validate and combine country code + phone BEFORE starting session
            let customerNumber = this.state.phone;
            if (type === 'sms') {
                // Check if phone number is required and provided
                if (!this.state.phone || this.state.phone.trim() === '') {
                    this.setCustomError({message: 'INVITE.VIEW.INCORRECT_NUMBER', level: 'error'});
                    return;
                }

                // Validate phone number format
                if (patterns.mobilePhone) {
                    const phoneRegex =
                        patterns.mobilePhone instanceof RegExp
                            ? patterns.mobilePhone
                            : new RegExp(String(patterns.mobilePhone).replace(/^\/|\/$/g, ''));
                    if (!phoneRegex.test(this.state.phone)) {
                        this.setCustomError({message: 'INVITE.VIEW.INCORRECT_NUMBER', level: 'error'});
                        return;
                    }
                }

                const validated = this._validateCountryCodeAndPhone(countryCode, this.state.phone, addCountryCodeToSms);
                if (!validated) {
                    return;
                }
                customerNumber = validated;
            }

            // Set status message only after validation passes
            this.setStatusMessage('INVITE.CONTROLLER.STARTING_SESSION');

            const roomData: any = {
                customerId: this.state.customerId || undefined,
                customerNumber: customerNumber || undefined,
                customerCountryCode: countryCode || 'none',
                clientLanguage: this._getClientLanguage(),
                initiateWithAudio: shouldStartWithAudio,
                offline: this.state.sessionType === SESSION_TYPES.OFFLINE
            };

            if (
                this.state.sessionType &&
                this.state.sessionType !== SESSION_TYPES.LIVE &&
                this.state.sessionType !== SESSION_TYPES.OFFLINE
            ) {
                roomData.startWithAgentType = this.state.sessionType;
            }

            if (isTechnicianSupervisor) {
                roomData.flowType = FlowType.fs;
            }

            const createRoomResponse = await this.roomService.createRoom(roomData);
            const room = (createRoomResponse as any)?.data ?? createRoomResponse;

            this.eventLogService.room = room._id;

            // For SMS, send via backend service
            if (type === 'sms') {
                this.eventLogService.SMSInvited({method: 'TechSee SMS'});
                await this._sendSMS(this.state.phone, room._id, countryCode);
            } else {
                // For email/whatsapp, use native app
                // @ts-ignore - CLIENT_START_URL is a webpack global constant
                const clientStartURL = (TsUtils as any).replaceUrlDomain(
                    CLIENT_START_URL,
                    this.window.location.hostname
                );

                const shortUrlData: any = {
                    roomId: room._id,
                    chromeDetection: Boolean(get(this.accountData, 'protectedSettings.chromeDetection')),
                    cameraModeOnly: Boolean(get(this.accountData, 'settings.cameraModeOnly')),
                    mobileClientURL: clientStartURL,
                    audio: shouldStartWithAudio,
                    offline: this.state.sessionType === SESSION_TYPES.OFFLINE
                };

                if (isTechnicianSupervisor) {
                    shortUrlData.flowType = FlowType.fs;
                }

                const shortUrlRes = await this.shortUrlService.createShortUrl(shortUrlData);

                const text = this.translate('INVITE.VIEW.GO_TO_MEETING');
                const linkText = encodeURIComponent(`${text}: ${shortUrlRes.url}`);

                let triggerUrl: string;
                if (type === 'whatsapp') {
                    triggerUrl = `whatsapp://send?text=${linkText}`;
                } else {
                    triggerUrl = `mailto:?body=${linkText}`;
                    this.eventLogService.emailInvited();
                }

                this.window.location.href = triggerUrl;

                setTimeout(() => {
                    this._gotoDashboard(room._id, shortUrlRes.shortId);
                }, 1000);
            }
        } catch (error) {
            let errorMessage = 'INVITE.CONTROLLER.FAILED_START_SESSION';

            if (get(error, 'status') === 402 || get(error, 'response.status') === 402) {
                errorMessage = 'INVITE.CONTROLLER.ERROR_101';
            }

            this.setCustomError({
                message: errorMessage,
                level: 'error'
            });
            this.setStatusMessage(undefined);
            this.setSms(false);
        }
    }

    private _gotoDashboard(roomId: string, roomShortId?: string): Promise<void> {
        const showRoomIdInUrl = Boolean(get(this.accountData, 'protectedSettings.showRoomIdInUrl'));

        // Match AngularJS: if no roomShortId and showRoomIdInUrl is false, fetch it first
        if (!roomShortId && !showRoomIdInUrl) {
            return this.roomService
                .getRoomShortId(roomId)
                .then((data: string | {shortId?: string; _id?: string}) => {
                    const shortId = typeof data === 'string' ? data : (data as any)?.shortId;
                    this._doRedirection(roomId, shortId);
                })
                .catch(() => {
                    // If fetch fails, use roomId anyway
                    this._doRedirection(roomId, undefined);
                });
        }

        this._doRedirection(roomId, roomShortId);

        return Promise.resolve();
    }

    private _doRedirection(roomId: string, roomShortId?: string): void {
        const audioForDashboard = this._shouldStartWithAudio();
        const normalizedUrl = (TsUtils as any).replaceUrlDomain(DASHBOARD_URL, this.window.location.hostname);
        const showRoomIdInUrl = Boolean(get(this.accountData, 'protectedSettings.showRoomIdInUrl'));
        const roomParam = showRoomIdInUrl ? roomId : roomShortId || roomId;

        const url = normalizedUrl
            .replace('{room}', roomParam)
            .replace('{observe}', '')
            .replace('{audio}', audioForDashboard ? 'yes' : '')
            .replace('{roomCode}', '');

        // Use window.location directly like AngularJS does for mobile
        this.window.location = url as any;
    }

    async joinByCode(code: string): Promise<void> {
        // Clear any previous errors
        this.setCustomError(undefined);

        let roomCode = code.toLowerCase();
        const guidPrefix = String(get(this.state.accountData, 'guidPrefix') || '');

        if (guidPrefix && roomCode.substr(0, guidPrefix.length) !== guidPrefix) {
            roomCode = `${guidPrefix}${roomCode}`;
        }

        try {
            // Validate the room code by trying to fetch the room by shortId
            // This matches the old AngularJS pattern: roomsService.find(sessionId, {params: {byShortId: true}})
            // Security: The API should validate account ownership, but we also check room.accountId as a safeguard
            const currentAccountId = get(this.state.accountData, '_id') as string | undefined;

            let room;
            try {
                room = await this.roomService.getById(roomCode, true); // true = byShortId

                // If room is null/undefined, the code is invalid
                if (!room) {
                    this.setCustomError({
                        message: 'INVITE.CONTROLLER.INVALID_SESSION_ID',
                        level: 'error'
                    });
                    return;
                }

                // Security check: Validate that the room belongs to the current account
                // The API should handle this, but we add an extra safeguard
                const roomAccountId = (room as any)?.accountId;
                if (currentAccountId && roomAccountId && roomAccountId !== currentAccountId) {
                    this.setCustomError({
                        message: 'INVITE.CONTROLLER.INVALID_SESSION_ID',
                        level: 'error'
                    });
                    return;
                }
            } catch (roomError: any) {
                // Room doesn't exist, belongs to different account, or error fetching it - invalid code
                // Note: API should return 403/404 for cross-account access, which we catch here
                this.setCustomError({
                    message: 'INVITE.CONTROLLER.INVALID_SESSION_ID',
                    level: 'error'
                });
                return;
            }

            // For observing, redirect to dashboard entry with observe=true
            // Use roomCode (shortId) as the session identifier
            const sessionId = roomCode;

            // Try to add observation record (may fail for some accounts, continue anyway)
            try {
                const observationData = {
                    shortId: sessionId,
                    userName: this.currentUser?.userName || '',
                    firstName: this.currentUser?.firstName || '',
                    lastName: this.currentUser?.lastName || '',
                    addNewSegment: true
                };
                await this.observationsService.addObservation(observationData);
            } catch (obsError) {
                // Continue even if observation fails - some accounts might not require it
            }

            // Redirect to dashboard entry for observing
            const redirectParams = {
                room: encodeURIComponent(sessionId),
                observe: 'true',
                ...(this.state.enableAudio && {audio: 'yes'})
            };
            this.redirectionService.goToDashboardEntry(redirectParams);
        } catch (error) {
            // Handle any errors from roomService.code or redirection
            this.setCustomError({
                message: 'INVITE.CONTROLLER.INVALID_SESSION_ID',
                level: 'error'
            });
        }
    }

    private async _joinAsSupervisor(roomId: string): Promise<void> {
        if (!roomId) {
            this.setCustomError({
                message: 'INVITE.CONTROLLER.SELECT_MEETING_TO_SUPERVISE',
                level: 'error'
            });
            return;
        }

        // Security: Validate that roomId is in the authorized observableUsers list
        const selectedObservableUser = this.state.observableUsers?.find((obs) => String(obs.roomId) === roomId);

        if (!selectedObservableUser) {
            this.setCustomError({
                message: 'INVITE.CONTROLLER.SELECT_MEETING_TO_SUPERVISE',
                level: 'error'
            });
            return;
        }

        try {
            const room = await this.roomService.getById(roomId, false);
            if (room) {
                // Add observation before redirecting (matching new invite desktop behavior)
                // sessionId is always present in IObservableByAccountResponse
                const sessionId = selectedObservableUser.sessionId;
                const observationData = {
                    shortId: sessionId,
                    userName: this.currentUser?.userName || '',
                    firstName: this.currentUser?.firstName || '',
                    lastName: this.currentUser?.lastName || '',
                    addNewSegment: true
                };
                try {
                    await this.observationsService.addObservation(observationData);
                } catch (obsError) {
                    // Continue even if observation fails - some accounts might not require it
                }
                const redirectParams = {
                    room: encodeURIComponent(sessionId),
                    observe: 'true',
                    ...(this.state.enableAudio && {audio: 'yes'})
                };
                // Use the redirection service which handles AngularJS routing properly
                this.redirectionService.goToDashboardEntry(redirectParams);
            }
        } catch (error) {
            this.setCustomError({
                message: 'INVITE.CONTROLLER.SELECT_MEETING_TO_SUPERVISE',
                level: 'error'
            });
        }
    }

    async superviseSession(): Promise<void> {
        await this._joinAsSupervisor(this.state.observedRoomId);
    }
}
